/**
 * Verified users and current user roles (for employee assignment and settings).
 */

import { API_BASE } from '../config/apiBase';
import { authHeaders, getSessionToken, SessionExpiredError, throwApiError } from './session';
import { normalizeDashboardSections, type DashboardSectionId } from '../utils/dashboardSectionPrefs';

export type DashboardSectionsMap = Record<DashboardSectionId, boolean>;

export interface VerifiedUser {
  id: number;
  username: string;
  email: string;
  roles?: string[];
  /** Set when TFRI has deactivated the account; null/absent means they can sign in. */
  deactivated_at?: string | null;
}

export const ACCOUNT_DEACTIVATED_MESSAGE =
  'Your account is deactivated. Please contact an admin to reactivate your account.';

export class AccountDeactivatedError extends Error {
  constructor(message = ACCOUNT_DEACTIVATED_MESSAGE) {
    super(message);
    this.name = 'AccountDeactivatedError';
  }
}

export function isUserDeactivated(u: { deactivated_at?: string | null }): boolean {
  return u.deactivated_at != null && String(u.deactivated_at).trim() !== '';
}

/** All selectable role values (DB/store). Admin is only assignable by an existing admin. */
export const ROLE_OPTIONS = [
  'admin',
  'accounting',
  'sales',
  'marketing',
  'engineers',
  'installation',
  'logistics',
  'corporate',
  'r_d',
  'support',
  'customer_service',
  'it',
  'operations',
  'finances',
  'manufacturing',
  'hr',
  'nuwa',
  'to_do_r',
  'carryoutsupplies',
  'legal',
  'procurement',
  'software',
  'hardware',
  'ried',
  'elevator',
] as const;

/** Roles a user can assign to themselves in Settings (excludes admin; admin only via Employees page). */
export const SELF_ASSIGNABLE_ROLES: readonly string[] = ROLE_OPTIONS.filter((r) => r !== 'admin');

/** Fetch current user profile with fresh roles from server (e.g. after another admin grants you a role). */
export async function fetchCurrentUser(userId: number): Promise<{ id: number; username: string; email: string; roles: string[] }> {
  const res = await fetch(`${API_BASE}/api/users/me?userId=${encodeURIComponent(userId)}`);
  const data = await res.json().catch(() => ({}));
  if (res.status === 403 && (data as { deactivated?: boolean; code?: string }).deactivated) {
    throw new AccountDeactivatedError(
      (data as { error?: string }).error || ACCOUNT_DEACTIVATED_MESSAGE
    );
  }
  if (!res.ok) throw new Error((data as { error?: string }).error || 'Failed to fetch user');
  return (data as { user: { id: number; username: string; email: string; roles: string[] } }).user;
}

/** Load saved dashboard section visibility for this user (null = never saved, use client defaults). */
export async function fetchDashboardSections(userId: number): Promise<unknown | null> {
  const res = await fetch(
    `${API_BASE}/api/users/me/dashboard-sections?userId=${encodeURIComponent(userId)}`
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || 'Failed to load dashboard layout');
  return (data as { sections?: unknown }).sections ?? null;
}

/** Persist dashboard section visibility (synced across devices for this account). */
export async function updateDashboardSections(
  userId: number,
  sections: DashboardSectionsMap
): Promise<DashboardSectionsMap> {
  const res = await fetch(`${API_BASE}/api/users/me/dashboard-sections`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId, sections }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || 'Failed to save dashboard layout');
  const raw = (data as { sections?: unknown }).sections;
  if (!raw || typeof raw !== 'object') {
    throw new Error('Invalid response from server');
  }
  return normalizeDashboardSections(raw);
}

export async function fetchVerifiedUsers(opts?: { includeDeactivated?: boolean }): Promise<VerifiedUser[]> {
  const qs = opts?.includeDeactivated ? '?include_deactivated=1' : '';
  const res = await fetch(`${API_BASE}/api/users/verified${qs}`);
  if (!res.ok) throw new Error('Failed to fetch users');
  const data = await res.json();
  return data.users || [];
}

export async function updateMyRoles(userId: number, roles: string[]): Promise<{ roles: string[] }> {
  const res = await fetch(`${API_BASE}/api/users/me`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId, roles }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to update roles');
  return data;
}

/**
 * Rename yourself. Session-authenticated, since it changes how you appear to
 * everyone else — a token from an older sign-in raises SessionExpiredError so the
 * page can ask for a fresh one.
 */
export async function updateMyUsername(username: string): Promise<{ id: number; username: string; email: string }> {
  if (!getSessionToken()) throw new SessionExpiredError();
  const res = await fetch(`${API_BASE}/api/users/me/username`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ username }),
  });
  if (!res.ok) await throwApiError(res, 'Failed to update your name.');
  const data = (await res.json()) as { user: { id: number; username: string; email: string } };
  return data.user;
}

/** Admin only: set another user's roles (including admin). */
export async function updateUserRoles(
  targetUserId: number,
  roles: string[],
  adminUserId: number
): Promise<{ roles: string[] }> {
  const res = await fetch(`${API_BASE}/api/users/${targetUserId}/roles`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ admin_user_id: adminUserId, roles }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to update roles');
  return data;
}

/** TFRI only: deactivate or reactivate another employee. Deactivating signs them out. */
export async function setUserDeactivated(
  targetUserId: number,
  deactivated: boolean,
  adminUserId: number
): Promise<{ deactivated_at: string | null }> {
  const res = await fetch(`${API_BASE}/api/users/${targetUserId}/deactivation`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ admin_user_id: adminUserId, deactivated }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || 'Failed to update account status');
  return { deactivated_at: (data as { deactivated_at?: string | null }).deactivated_at ?? null };
}

/** TFRI (`admin` role) only: invite by email; sends a link to create password and join. Any email domain is allowed. */
export async function inviteEmployee(email: string, adminUserId: number): Promise<{ message: string }> {
  const res = await fetch(`${API_BASE}/api/auth/invite`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email: email.trim().toLowerCase(), admin_user_id: adminUserId }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || 'Failed to send invite');
  return data;
}
